/**
 * @fileoverview Unified Statistics Service with new backend API integration.
 * @module lib/statisticsService
 */

import apiClient from './apiClient'

/**
 * Represents a single variant/product.
 */
export interface Variant {
  /** Unique variant identifier */
  id: number
  /** Display name */
  name: string
  /** SKU key used as dimension key in statistics */
  variantSkuKey: string
  /** Product-level name (parent product family) */
  productName: string | null
  /** Variant image URL */
  imageUrl: string | null
  /** Product description */
  description: string
  /** Product category */
  category: string
  /** Brand name */
  brand: string
  /** Model identifier */
  model: string
  /** Base cost price */
  cost: number
  /** Currency information for cost */
  costCurrency: {
    /** Currency code (e.g., 'USD') */
    currencyCode: string
    /** Numeric currency code */
    numericCode: number
    /** Numeric code as string */
    numericCodeAsString: string
    /** Human-readable currency name */
    displayName: string
    /** Currency symbol (e.g., '$') */
    symbol: string
    /** Default decimal places */
    defaultFractionDigits: number
  }
}

/** @deprecated Use Variant instead */
export type SKU = Variant

/**
 * Statistics dimensions for 3D grouping.
 */
export type StatisticsDimension = 'COUNTRY' | 'PRODUCT' | 'TIME'

/**
 * Business view types for different analytics purposes.
 */
export type StatisticsView = 'SALES_ANALYTICS' | 'CANCELLATION_ANALYTICS' | 'AUDIT'

/**
 * Time aggregation levels.
 */
export type TimeAggregation = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY'

/**
 * Filter parameters for unified statistics API calls.
 */
export type StatisticsMode = 'SALES_ONLY' | 'CANCELLATIONS_ONLY' | 'BOTH'

export interface AllocationOptions {
  /** Indirect cost allocation strategy */
  strategy: 'TIME_ONLY'
}

export interface UnifiedStatisticsFilterDTO {
  /** Start date in YYYY-MM-DD format */
  startDate?: string
  /** End date in YYYY-MM-DD format */
  endDate?: string
  /** Array of country codes to filter by */
  countries?: string[]
 /** Array of SKU identifiers to filter by */
  skus?: string[]
  /** Array of product tag UUIDs to filter by */
  tags?: string[]
  /** Grouping granularity for product dimension */
  group?: 'PRODUCT' | 'VARIANT'
  /** Time aggregation level when TIME dimension is used */
  timeAggregation?: TimeAggregation
  /** Whether to include allocated indirect costs in responses */
  includeAllocated?: boolean
  /** Allocation options; serialized as `allocation.strategy` in query */
  allocation?: AllocationOptions
}

/**
 * Single indirect cost group with optional per-bucket amounts.
 *
 * Example (DAILY): byTime = { '2025-09-01': 166.8333, '2025-09-02': 166.8333 }
 * Example (MONTHLY): byTime = { '2025-09': 1500.0000, '2025-10': 1600.0000 }
 */
export interface IndirectCostGroupDTO {
  /** Human-readable group label (e.g. "Salaries", "InFakt", "(Unspecified)") */
  label: string
  /** Category of the indirect cost (e.g. 'OPEX') */
  category: string
  /** Stable indirect cost subcategory UUID */
  subcategoryUuid?: string
  /** Display name of the linked subcategory */
  subcategoryDisplayName?: string
  /** Slug of the linked subcategory */
  subcategorySlug?: string
  /** Whether the subcategory is system-protected (e.g., Uncategorized) */
  subcategorySystem?: boolean
  /** Total cost for the entire window (scaled to 4 decimals by BE) */
  total: number
  /** Share in percent for the entire window (scaled to 2 decimals by BE) */
  sharePercent: number
  /** Number of entries aggregated into this group */
  entryCount: number
  /** Optional list of entry UUIDs composing this group */
  entryUuids?: string[]
  /** Map of time bucket -> amount as per response.timeAggregation */
  byTime: Record<string, number>
}

/**
 * Optional indirect costs dataset present when includeAllocated=true.
 */
export interface IndirectCostsDTO {
  /** Total indirect costs across the entire response window */
  total: number
  /** Top-N limit applied by backend (if any) */
  topN?: number | null
  /** Backend grouping strategy label (e.g. 'DESCRIPTION_CATEGORY') */
  grouping: string
  /** Time aggregation for byTime keys (matches request) */
  timeAggregation?: TimeAggregation
  /** Primary groups sorted descending by total (index order is rank) */
  groups: IndirectCostGroupDTO[]
  /** Optional aggregated total for items beyond Top-N */
  otherTotal?: number | null
  /** Optional time series for the others bucket */
  othersByTime?: Record<string, number> | null
}

/**
 * Single member of a collection-allocation group: a product that receives a
 * share of the collection-URL ad spend, with its allocated amount and share.
 */
export interface CollectionAllocationMemberDTO {
  /** Internal variant identifier */
  variantId: number
  /** Variant SKU key */
  variantSku: string
  /** Product external id (matches FE product.id / route :productId) */
  productKey: string
  /** Ad spend allocated to this member (reporting currency) */
  allocated: number
  /** Share of the group's total allocation, in percent */
  sharePercent: number
}

/**
 * Collection-allocation group: one collection URL (resolved to a product tag)
 * whose ad spend is distributed across member products by revenue share.
 */
export interface CollectionAllocationGroupDTO {
  /** Canonical collection URL path (e.g. '/collections/nail-drills') */
  canonicalPath: string
  /** Stable product-tag UUID for the collection */
  tagUuid: string
  /** Human-readable tag/collection name */
  tagName: string
  /** Total ad spend allocated across all members (reporting currency) */
  totalAllocated: number
  /** Members receiving a share of this group's allocation */
  members: CollectionAllocationMemberDTO[]
}

/**
 * Collection-allocation dataset present on SALES_ANALYTICS responses.
 *
 * IMPORTANT: shares are computed from the sales the backend is given, which are
 * filtered by the query's `skus`/`tags` BEFORE allocation. A product-filtered
 * statistics call therefore mis-computes shares — always source this from an
 * ORG-WIDE (no `skus`, no `tags`) statistics response and filter on the FE.
 */
export interface CollectionAllocationDTO {
  /** Allocation groups, one per collection URL */
  groups: CollectionAllocationGroupDTO[]
}

/**
 * Calculated metrics for a statistics data point.
 */
export interface StatisticsMetricsDTO {
  /** Total sales amount */
  sales?: number
  /** Total quantity sold */
  quantity?: number
  /** Units belonging to fully-cancelled orders (subset of quantity, since sales are gross) */
  cancelledQuantity?: number
  /** Total advertising spend */
  advertising?: number
  /** Advertising spend breakdown by platform */
  platformBreakdown?: Record<string, number>
  /** Cost of goods sold */
  cogs?: number
  /** Direct costs (shipping, packaging, handling, etc.) */
  directCosts?: number
  /** Contribution margin 2 (sales - advertising - cogs - directCosts) */
  cm2?: number
  /** Return on advertising spend (sales / advertising) */
  roas?: number
  /** CM2 margin (cm2 / sales) */
  cm2Margin?: number
  /** Order count for this data point */
  orderCount?: number
  /** Allocated indirect costs (if includeAllocated=true) */
  allocatedIndirectCosts?: number
  /** Allocated operating contribution (cm2 - allocatedIndirectCosts) */
  allocatedOperatingContribution?: number
  /** Allocated operating contribution margin (ratio) */
  allocatedOperatingContributionMargin?: number
  /** Number of cancelled/returned orders (CANCELLATION_ANALYTICS only) */
  cancellationCount?: number
  /** Return rate percentage (CANCELLATION_ANALYTICS only) */
  returnRate?: number
  /** Potential revenue lost due to cancellations (CANCELLATION_ANALYTICS only) */
  potentialRevenueLoss?: number
}

/**
 * Collapsed dimension group metadata keyed by dimension + group slug.
 */
export interface StatisticsDimensionGroup {
  /** Members belonging to the group (anchor included) */
  members: string[]
  /** Optional mapping of member identifiers to display labels */
  memberLabels?: Record<string, string>
  /** Identifier of the anchor SKU for the group */
  anchorSku?: string
  /** Human-readable group name */
  name?: string
  /** Optional image representing the group */
  imageUrl?: string | null
  /** Total number of members in the group */
  memberCount?: number
}

/**
 * Single data point with dimensions and calculated metrics.
 */
export interface StatisticsDataPointDTO {
  /** Dimension values for this data point */
  dimensions: Record<string, unknown>
  /** Calculated metrics for this data point */
  metrics: StatisticsMetricsDTO
}

/**
 * Summary metadata for the statistics dataset.
 */
export interface StatisticsMetadataDTO {
  /** Total sales across all data points */
  totalSales?: number
  /** Total CM2 across all data points */
  totalCm2?: number
  /** Total advertising spend across all data points */
  totalAdvertising?: number
  /** Total cost of goods sold across all data points */
  totalCogs?: number
  /** Total direct costs across all data points */
  totalDirectCosts?: number
  /** Total allocated indirect costs across all data points (if included) */
  totalAllocatedIndirectCosts?: number
  /** Total allocated operating contribution (cm2 - allocatedIndirectCosts) */
  totalAllocatedOperatingContribution?: number
  /** Total number of orders across all data points */
  totalOrderCount?: number
  /** Total number of data points in the response */
  totalDataPoints?: number
  /** List of order statuses included in this view */
  includedStatuses?: string[]
  /** Overall ROAS for the dataset */
  overallRoas?: number
  /** Overall CM2 margin for the dataset */
  overallCm2Margin?: number
  /** Total number of cancellations (CANCELLATION_ANALYTICS only) */
  totalCancellations?: number
  /** Overall return rate (CANCELLATION_ANALYTICS only) */
  overallReturnRate?: number
  /** Total revenue lost to cancellations (CANCELLATION_ANALYTICS only) */
  totalRevenueLoss?: number
  /** Indicates whether allocated costs are included */
  includesAllocated?: boolean
  /** Allocation details when applicable */
  allocation?: {
    strategy: 'TIME_ONLY'
    /** Optional backend bucket used for indirect costs */
    indirectCostsBucket?: string
  }
  /** Optional policy identifier used to compute results */
  policyId?: string
  /**
   * Human-readable labels for dimension keys.
   *
   * Shape: { [dimension: string]: { [key: string]: string } }
   * Example: labels.product['ab-pro-two'] -> 'AbyssBlock Pro 2 - …'
   */
  labels?: Record<string, Record<string, string>>
  /**
   * Optional numeric IDs map for dimension keys (when available).
   * Useful for stable cross-resource linking; may be null/undefined.
   */
  ids?: Record<string, Record<string, number>> | null
  /**
   * Locale of labels returned by backend (e.g., 'en-US').
   */
  labelsLocale?: string
  /**
   * Optional grouping metadata keyed by dimension then group key.
   * Example: dimensionGroups.product['grp:collection'] -> group definition.
   */
  dimensionGroups?: Record<string, Record<string, StatisticsDimensionGroup>>
}

/**
 * Unified statistics response with flexible 3D grouping.
 */
export interface UnifiedStatisticsResponseDTO {
  /** Aggregation mode used for computation */
  mode?: StatisticsMode
  /** Business view type */
  view: StatisticsView
  /** Grouping dimensions applied to data */
  groupBy: StatisticsDimension[]
  /** Time aggregation level when TIME dimension is used */
  timeAggregation?: TimeAggregation
  /** Summary metadata for the dataset */
  metadata: StatisticsMetadataDTO
  /** Array of data points with dimensions and metrics */
  data: StatisticsDataPointDTO[]
  /** Optional indirect costs dataset when includeAllocated=true */
  indirectCosts?: IndirectCostsDTO
  /** Optional collection-allocation dataset (collection-URL ad-spend distribution) */
  collectionAllocation?: CollectionAllocationDTO
}

/**
 * Helper function to build query parameters for API calls.
 *
 * @param filter - Filter parameters
 * @param groupBy - Dimensions to group by
 * @param timeAggregation - Time aggregation level
 * @returns Query parameters object
 */
function buildQueryParams(
  filter: UnifiedStatisticsFilterDTO,
  groupBy: StatisticsDimension[],
  timeAggregation?: TimeAggregation
): Record<string, unknown> {
  const params: Record<string, unknown> = { ...filter }

  const hasProductDimension = groupBy.includes('PRODUCT')

  // collapse is deprecated backend-side; never send
  if ('collapse' in params) {
    delete params.collapse
  }

  // Explicitly set grouping granularity only when PRODUCT dimension is present
  if (hasProductDimension) {
    params.group = filter.group ?? 'PRODUCT'
  } else if ('group' in params) {
    delete params.group
  }

  // Add groupBy dimensions
  if (groupBy.length > 0) {
    params.groupBy = groupBy
  }

  // Add time aggregation if specified
  if (timeAggregation) {
    params.timeAggregation = timeAggregation
  }

  // Serialize allocation.strategy as dotted query param and remove nested object
  if (filter.allocation?.strategy) {
    params['allocation.strategy'] = filter.allocation.strategy
  }
  if (params.allocation) {
    delete params.allocation
  }

  // Only include includeAllocated when true to avoid sending explicit false
  if (typeof params.includeAllocated !== 'undefined' && params.includeAllocated !== true) {
    delete params.includeAllocated
  }

  // Handle array parameters: drop empty arrays
  if (params.countries && Array.isArray(params.countries) && params.countries.length === 0) {
    delete params.countries
  }
  if (params.skus && Array.isArray(params.skus) && params.skus.length === 0) {
    delete params.skus
  }
  if (params.tags && Array.isArray(params.tags) && params.tags.length === 0) {
    delete params.tags
  }

  return params
}

/**
 * Unified Statistics Service with new backend API integration.
 *
 * @example
 * ```typescript
 * import { unifiedStatisticsService } from '@/lib/statisticsService';
 *
 * // Get sales analytics with time dimension
 * const salesOverTime = await unifiedStatisticsService.getSalesStatistics(
 *   { startDate: '2024-01-01', endDate: '2024-01-31' },
 *   ['TIME'],
 *   'MONTHLY'
 * );
 *
 * // Get sales analytics with country and product dimensions
 * const salesByCountryProduct = await unifiedStatisticsService.getSalesStatistics(
 *   { countries: ['PL', 'SK'] },
 *   ['COUNTRY', 'PRODUCT']
 * );
 * ```
 */
export const unifiedStatisticsService = {
  /**
   * Fetch sales statistics with flexible 3D grouping via unified endpoint.
   *
   * @param filter - Filter parameters (dates, geography, SKUs, collapse controls, allocation)
   * @param groupBy - Dimensions to group by
   * @param timeAggregation - Time aggregation level (required when TIME dimension is used)
   * @returns Promise resolving to unified statistics response
   * @throws API error if request fails
   */
  getSalesStatistics: async (
    filter: UnifiedStatisticsFilterDTO = {},
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ): Promise<UnifiedStatisticsResponseDTO> => {
    const baseParams = buildQueryParams(filter, groupBy, timeAggregation)
    const params = { mode: 'SALES_ONLY' as StatisticsMode, ...baseParams }
    const response = await apiClient.get('/statistics', {
      params,
      paramsSerializer: {
        indexes: null, // arrays as repeated keys: groupBy=TIME&groupBy=PRODUCT
      },
    })
    return response.data
  },

  /**
   * Fetch cancellation statistics with flexible 3D grouping via unified endpoint.
   *
   * @param filter - Filter parameters (dates, geography, SKUs, collapse controls, allocation)
   * @param groupBy - Dimensions to group by
   * @param timeAggregation - Time aggregation level (required when TIME dimension is used)
   * @returns Promise resolving to unified statistics response
   * @throws API error if request fails
   */
  getCancellationStatistics: async (
    filter: UnifiedStatisticsFilterDTO = {},
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ): Promise<UnifiedStatisticsResponseDTO> => {
    const baseParams = buildQueryParams(filter, groupBy, timeAggregation)
    const params = { mode: 'CANCELLATIONS_ONLY' as StatisticsMode, ...baseParams }
    const response = await apiClient.get('/statistics', {
      params,
      paramsSerializer: {
        indexes: null,
      },
    })
    return response.data
  },

  /**
   * Fetch merged sales + cancellation statistics in a single call.
   * Returns both profitability and cancellation metrics per dimension key.
   *
   * @param filter - Filter parameters (dates, geography, SKUs, collapse controls, allocation)
   * @param groupBy - Dimensions to group by
   * @param timeAggregation - Time aggregation level (required when TIME dimension is used)
   * @returns Promise resolving to unified statistics response with both sales and cancellation metrics
   * @throws API error if request fails
   */
  getBothStatistics: async (
    filter: UnifiedStatisticsFilterDTO = {},
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ): Promise<UnifiedStatisticsResponseDTO> => {
    const baseParams = buildQueryParams(filter, groupBy, timeAggregation)
    const params = { mode: 'BOTH' as StatisticsMode, ...baseParams }
    const response = await apiClient.get('/statistics', {
      params,
      paramsSerializer: {
        indexes: null,
      },
    })
    return response.data
  },

  /**
   * Fetch audit statistics with flexible 3D grouping (admin only).
   *
   * @param filter - Filter parameters (dates, geography, SKUs, collapse controls, allocation)
   * @param groupBy - Dimensions to group by
   * @param timeAggregation - Time aggregation level (required when TIME dimension is used)
   * @returns Promise resolving to unified statistics response
   * @throws API error if request fails or user lacks admin privileges
   */
  getAuditStatistics: async (
    filter: UnifiedStatisticsFilterDTO = {},
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ): Promise<UnifiedStatisticsResponseDTO> => {
    const params = buildQueryParams(filter, groupBy, timeAggregation)
    const response = await apiClient.get('/statistics/audit', {
      params,
      paramsSerializer: {
        indexes: null,
      },
    })
    return response.data
  },

  /**
   * Fetch all available variants from the system.
   *
   * @returns Promise resolving to array of Variant objects
   * @throws API error if request fails
   */
  getAllVariants: async (): Promise<Variant[]> => {
    const response = await apiClient.get('/variants', {
      params: { page: 0, size: 1000, sort: 'variantSkuKey', direction: 'asc', includeSystem: false },
    })
    const data = response.data
    if (Array.isArray(data)) return data
    if (Array.isArray(data?.content)) return data.content
    return []
  },

  /** @deprecated Use getAllVariants instead */
  getAllSkus: async (): Promise<Variant[]> => {
    return statisticsService.getAllVariants()
  },

  /**
   * Get AI-powered insights for product statistics.
   *
   * @param filter - Filter parameters for data scope
   * @param prompt - AI prompt for analysis
   * @returns Promise resolving to AI analysis text
   * @throws API error if request fails
   */
  getAiInsights: async (
    filter: UnifiedStatisticsFilterDTO = {},
    prompt: string
  ): Promise<string> => {
    const queryParams = { prompt }
    const requestBody = { ...filter }
    const response = await apiClient.post('/ai/product-by-country', requestBody, {
      params: queryParams,
    })
    return response.data
  },
}

// Export main service as default export and named export
export const statisticsService = unifiedStatisticsService
export default unifiedStatisticsService
